import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Upload,
  message,
} from "antd";
import type { UploadFile } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import {
  GetAllMyStoresDocument,
  useCitiesLookupQuery,
  useCreateStoreMutation,
  useGetAllMyWorkingHoursQuery,
  useRegionsLookupQuery,
  useUpdateStoreMutation,
  useUploadFileMutation,
} from "../graphql/generated/hooks";
import { useForm } from "antd/es/form/Form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import RobinPage from "../components/core/RobinPage";
import { useEffect, useState } from "react";
import MapComponent from "../components/map/MapComponent";
import { DEFAULT_LAT, DEFAULT_LNG } from "../common/constants";
import ResizableCropModal from "../components/ResizableCropModal";

function StoreMutate() {
  const { t, i18n } = useTranslation();
  const [form] = useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const navigate = useNavigate();
  const params = useParams();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(false);
  const [ready, setReady] = useState<boolean>(false);
  const [locationImageFileList, setLocationImageFileList] = useState<
    UploadFile[]
  >([]);
  const [uploadedLocationImageId, setUploadedLocationImageId] = useState<
    number | null
  >(null);
  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState("");

  const normalizeSaudiPhoneNational = (value: unknown) => {
    const digitsOnly =
      typeof value === "string" ? value.replace(/\D/g, "") : "";
    // With +966 prefix UI we only want the national part (9 digits), without leading 0.
    return digitsOnly.replace(/^0+/, "").slice(0, 9);
  };

  const toSaudiE164Digits = (national: unknown) => {
    const normalized = normalizeSaudiPhoneNational(national);
    return normalized ? `966${normalized}` : "";
  };

  const fromStoredSaudiPhoneToNational = (stored: unknown) => {
    const digitsOnly =
      typeof stored === "string" ? stored.replace(/\D/g, "") : "";
    // Accept stored formats: 05xxxxxxxx, 0xxxxxxxxx (landline), 966xxxxxxxxx, +966xxxxxxxxx
    const withoutCountry = digitsOnly.startsWith("966")
      ? digitsOnly.slice(3)
      : digitsOnly;
    return withoutCountry.replace(/^0+/, "").slice(0, 9);
  };

  const [locationData, setLocationData] = useState<{
    latitude: number;
    longitude: number;
  }>({ latitude: DEFAULT_LAT, longitude: DEFAULT_LNG });

  // Track region for cities query - updated when user selects region or when loading edit data
  const [regionIdForCities, setRegionIdForCities] = useState<
    string | undefined
  >(undefined);

  const [createStore, { loading: creating }] = useCreateStoreMutation();
  const [updateStore, { loading: updating }] = useUpdateStoreMutation();
  const [uploadFile, { loading: uploading }] = useUploadFileMutation();

  const { data: workingHours, loading: loadingWorkingHours } =
    useGetAllMyWorkingHoursQuery();

  const { data: regionsData, loading: loadingRegions } = useRegionsLookupQuery({
    fetchPolicy: "no-cache",
  });
  const { data: citiesData, loading: loadingCities } = useCitiesLookupQuery({
    variables: { regionId: regionIdForCities ?? "" },
    skip: !regionIdForCities,
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    if (params.id) {
      if (+params.id === location.state?.store?.id) {
        if (location.state.store.latitude && location.state.store.longitude)
          setLocationData({
            latitude: location.state.store.latitude,
            longitude: location.state.store.longitude,
          });
        if (location.state.store.regionId) {
          setRegionIdForCities(location.state.store.regionId);
        }
        if (location.state.store.locationImage) {
          setUploadedLocationImageId(location.state.store.locationImage.id);
          setLocationImageFileList([
            {
              uid: `-store-location-${location.state.store.locationImage.id}`,
              name: "Store Location Image",
              status: "done",
              url: location.state.store.locationImage.url,
            },
          ]);
        }
        setEditMode(true);
        setReady(true);
      } else {
        navigate(-1);
      }
    } else setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const convertFileToBase64 = (file: File | Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
    });
  };

  const handleFileSelect = (file: File) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      setCropImageUrl(event.target?.result as string);
      setCropModalOpen(true);
    };
    reader.readAsDataURL(file);
    return false;
  };

  const resetCropState = () => {
    setCropModalOpen(false);
    setCropImageUrl("");
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    try {
      const base64String = await convertFileToBase64(croppedBlob);
      const fileName = `store-location-${Date.now()}.jpg`;
      const result = await uploadFile({
        variables: {
          base64File: base64String,
          originalName: fileName,
          folder: "stores/location-images",
        },
      });

      if (!result.data?.uploadFile) {
        throw new Error("Upload failed: No data returned");
      }

      const uploaded = result.data.uploadFile;
      setUploadedLocationImageId(uploaded.id);
      setLocationImageFileList([
        {
          uid: `-store-location-${uploaded.id}`,
          name: fileName,
          status: "done",
          url: uploaded.url,
        },
      ]);
      resetCropState();
      messageApi.success(t("message.success.data_updated"));
    } catch {
      resetCropState();
      messageApi.error(t("message.error.data_update_failed"));
    }
  };

  const handleEditImage = async () => {
    const imageUrl = locationImageFileList[0]?.url;
    if (!imageUrl) return;

    if (imageUrl.startsWith("http://") || imageUrl.startsWith("https://")) {
      try {
        const res = await fetch(imageUrl, {
          mode: "cors",
          credentials: "omit",
        });
        if (!res.ok) throw new Error("Fetch failed");
        const blob = await res.blob();
        const dataUrl = await convertFileToBase64(blob);
        setCropImageUrl(dataUrl);
        setCropModalOpen(true);
      } catch {
        setCropImageUrl(imageUrl);
        setCropModalOpen(true);
      }
    } else {
      setCropImageUrl(imageUrl);
      setCropModalOpen(true);
    }
  };

  const onFinish = async (values: any) => {
    const phoneE164Digits = toSaudiE164Digits(values.phone);
    if (editMode)
      await updateStore({
        variables: {
          id: location.state.store.id,
          name: values.name,
          nameAr: values.nameAr,
          phone: phoneE164Digits,
          address: values.address,
          regionId: values.regionId,
          cityId: values.cityId,
          defaultWorkingHoursId: values.defaultWorkingHoursId,
          longitude: locationData.longitude,
          latitude: locationData.latitude,
          preparationTime: values.preparationTime,
          locationImageId: uploadedLocationImageId,
        },
        refetchQueries: [{ query: GetAllMyStoresDocument }],
        awaitRefetchQueries: true,
      })
        .then(() => {
          messageApi.success(t("message.success.data_updated"));
          navigate(-1);
        })
        .catch((error) => {
          messageApi.error(t("message.error.data_update_failed"));
        });
    else
      await createStore({
        variables: {
          name: values.name,
          nameAr: values.nameAr,
          phone: phoneE164Digits,
          address: values.address,
          regionId: values.regionId,
          cityId: values.cityId,
          defaultWorkingHoursId: values.defaultWorkingHoursId,
          longitude: locationData.longitude,
          latitude: locationData.latitude,
          preparationTime: values.preparationTime,
          locationImageId: uploadedLocationImageId,
        },
        refetchQueries: [{ query: GetAllMyStoresDocument }],
        awaitRefetchQueries: true,
      })
        .then(() => {
          messageApi.success(t("message.success.data_created"));
          navigate(-1);
        })
        .catch((error) => {
          messageApi.error(t("message.error.data_create_failed"));
        });
  };

  const uploadButton = (
    <button style={{ border: 0, background: "none" }} type="button">
      <PlusOutlined />
      <div style={{ marginTop: 8 }}>{t("button.upload")}</div>
    </button>
  );

  return (
    <RobinPage title={editMode ? t("title.edit_store") : t("title.add_store")}>
      <Row gutter={[20, 20]}>
        {contextHolder}
        {ready ? (
          <>
            <Col xl={12} sm={24}>
              <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
                dir={i18n.dir()}
                name="mutateStore"
                initialValues={
                  editMode
                    ? {
                        ...location.state.store,
                        phone: fromStoredSaudiPhoneToNational(
                          location.state.store.phone,
                        ),
                      }
                    : undefined
                }
              >
                <Form.Item
                  name="name"
                  label="Name"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message:
                        t("validation.store.name.required") ||
                        "Name is required",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="nameAr"
                  label="Name Ar"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message:
                        t("validation.store.nameAr.required") ||
                        "Arabic name is required",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="phone"
                  label="Phone"
                  normalize={normalizeSaudiPhoneNational}
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message:
                        t("validation.phone.required") || "Phone is required",
                    },
                    {
                      validator: async (_rule, value) => {
                        const raw =
                          typeof value === "string" ? value.trim() : "";
                        if (!raw) return Promise.resolve(); // handled by `required`

                        const saudiPhoneDigitsOnly =
                          /^(5\d{8}|[1-4,6-7]\d{8})$/;

                        if (
                          !/^\d+$/.test(raw) ||
                          !saudiPhoneDigitsOnly.test(raw)
                        ) {
                          return Promise.reject(
                            new Error(
                              t("validation.phone.invalid") ||
                                "Phone must be a valid Saudi mobile or landline number",
                            ),
                          );
                        }
                      },
                    },
                  ]}
                >
                  <Input
                    addonBefore="+966"
                    inputMode="numeric"
                    maxLength={9}
                    placeholder="5XXXXXXXX or 1XXXXXXXX"
                  />
                </Form.Item>
                <Form.Item
                  name="address"
                  label="Address"
                  rules={[
                    {
                      required: true,
                      whitespace: true,
                      message:
                        t("validation.address.required") ||
                        "Address is required",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  name="regionId"
                  label={t("label.region") || "Region"}
                  rules={[
                    {
                      required: true,
                      message:
                        t("validation.region.required") || "Region is required",
                    },
                  ]}
                >
                  <Select
                    key="region-select"
                    loading={loadingRegions}
                    placeholder={
                      t("placeholder.select_region") || "Select region"
                    }
                    options={
                      loadingRegions
                        ? []
                        : regionsData?.regionsLookup?.data?.map((region) => ({
                            value: region.id,
                            label:
                              (i18n.language === "ar"
                                ? region.nameAr
                                : region.nameEn) ||
                              region.nameAr ||
                              region.nameEn ||
                              region.id,
                          })) ?? []
                    }
                    onChange={(value) => {
                      form.setFieldValue("cityId", undefined);
                      setRegionIdForCities(value);
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name="cityId"
                  label={t("label.city") || "City"}
                  rules={[
                    {
                      required: true,
                      message:
                        t("validation.deliveryArea.required") ||
                        "City is required",
                    },
                  ]}
                >
                  <Select
                    key={regionIdForCities}
                    loading={loadingCities}
                    placeholder={t("placeholder.select_city") || "Select city"}
                    disabled={!regionIdForCities}
                    options={
                      loadingCities
                        ? []
                        : citiesData?.citiesLookup?.data?.map((city) => ({
                            value: city.id,
                            label:
                              (i18n.language === "ar"
                                ? city.nameAr
                                : city.nameEn) ||
                              city.nameAr ||
                              city.nameEn ||
                              city.id,
                          })) ?? []
                    }
                  />
                </Form.Item>
                <Form.Item
                  name="defaultWorkingHoursId"
                  label="Default Working Hours"
                >
                  <Select
                    loading={loadingWorkingHours}
                    placeholder={t("placeholder.select_stores")}
                    options={workingHours?.getAllWorkingHoursForPartner?.map(
                      (wh) => ({
                        value: wh.id,
                        label: wh.name,
                      }),
                    )}
                  />
                </Form.Item>
                <Form.Item
                  name="preparationTime"
                  label="Preparation Time (minutes)"
                >
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item
                  label={t("label.store.location_image") || "Location Image"}
                >
                  <Upload
                    listType="picture-card"
                    fileList={locationImageFileList}
                    beforeUpload={handleFileSelect}
                    onChange={({ fileList: newFileList }) => {
                      if (
                        newFileList[0]?.status === "done" ||
                        newFileList.length === 0
                      ) {
                        setLocationImageFileList(newFileList);
                      }
                    }}
                    onRemove={() => {
                      setUploadedLocationImageId(null);
                      setLocationImageFileList([]);
                    }}
                    onPreview={handleEditImage}
                    maxCount={1}
                    accept="image/*"
                  >
                    {locationImageFileList.length >= 1 ? null : uploadButton}
                  </Upload>
                </Form.Item>
                <Form.Item>
                  <Space>
                    <Button
                      onClick={() => {
                        navigate(-1);
                      }}
                    >
                      {t("button.cancel")}
                    </Button>
                    <Button
                      loading={creating || updating || uploading}
                      type="primary"
                      htmlType="submit"
                    >
                      {t("button.save")}
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Col>
            <Col xl={12} sm={24} style={{ minHeight: "50vh" }}>
              <MapComponent
                lat={locationData.latitude}
                lng={locationData.longitude}
                onLocationChange={(latlng) => {
                  setLocationData({
                    latitude: latlng.lat,
                    longitude: latlng.lng,
                  });
                }}
              />
            </Col>
          </>
        ) : (
          <Spin />
        )}
      </Row>
      <ResizableCropModal
        open={cropModalOpen}
        imageUrl={cropImageUrl}
        shape="rectangle"
        onCancel={resetCropState}
        onOk={handleCropComplete}
      />
    </RobinPage>
  );
}

export default StoreMutate;
